import React from 'react';
import { AopLink, Card, InternalCard, Pill, StatusPill, Sparkline, WarnRow } from '../ui';
import { account, commandCenter, internalOps, segments } from '../copData';
import { useCop } from '../CopApp';
import UpsellyMascot from '../UpsellyMascot';

const TONE_COLOR = {
	purple: 'var(--purple)',
	pink: 'var(--pink)',
	green: 'var(--green)',
	amber: 'var(--amber)',
	cyan: 'var(--cyan)',
};

const KPI_BAR_COLORS = ['#ff007a', '#8b5cf6', '#4a6ee0'];

function KpiCard({ label, value, pill, pillTone = 'green', trend, pos }) {
	const max = Math.max(...trend);
	const min = Math.min(...trend);
	const range = max - min || 1;
	return (
		<div className={`cop-kpi-card pos-${pos}`}>
			<div className="cop-kpi-head">
				<span className="cop-kpi-label">{label}</span>
				<span className={`cop-kpi-pill ${pillTone}`}>{pill}</span>
			</div>
			<div className="cop-kpi-value">{value}</div>
			<div className="cop-kpi-bars">
				{trend.map((v, i) => (
					<span
						key={i}
						style={{
							height: 8 + ((v - min) / range) * 14,
							background: KPI_BAR_COLORS[i % KPI_BAR_COLORS.length],
						}}
					/>
				))}
			</div>
		</div>
	);
}

export default function CommandCenter() {
	const { approvals, decideApproval, navigate, isInternal, reportDateRangeLabel } = useCop();
	const pending = approvals
		.filter((a) => a.state === 'submitted' && (isInternal || a.stage === 'client'))
		.slice(0, 4);

	return (
		<main className="cop-page">
			<div>
				<h1 className="cop-page-title">Command Center</h1>
				<div className="cop-page-sub">
					{account.merchant_name} · {account.platform} · {reportDateRangeLabel} · data as of {account.data_as_of}
				</div>
			</div>

			<div className="cop-kpi-strip">
				<div className="cop-kpi-glow purple" />
				<div className="cop-kpi-glow magenta" />
				<div className="cop-kpi-mascot">
					<UpsellyMascot size={300} />
				</div>
				{commandCenter.stats.map((s, i) => (
					<KpiCard key={s.label} pos={i} {...s} />
				))}
			</div>

			<div className="cop-grid cop-cc-grid">
				<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					<Card title="AI Intelligence Brief" icon="✦" right="updated 12 min ago" className="cop-brief">
						<p>{commandCenter.brief}</p>
						<div className="cop-chip-row">
							<button className="cop-chip" onClick={() => navigate('clustering')}>Review Segment ↗</button>
							<button className="cop-chip" onClick={() => navigate('experiment-lab')}>Declare Winner ↗</button>
							<button className="cop-chip" onClick={() => navigate('clustering')}>Review Micro-Segments ↗</button>
						</div>
					</Card>

					<Card title="Active Segments" icon="✣" right={`${segments.length} discovered`}>
						<div className="cop-table-wrap">
						<table className="cop-table">
							<thead>
								<tr>
									<th>Segment</th>
									<th>Size</th>
									<th>Trend</th>
									<th>Conv.</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{segments.map((s) => (
									<tr key={s.segment_id} style={{ cursor: 'pointer' }} onClick={() => navigate('clustering')}>
										<td>
											<span className="cop-legend-dot" style={{ color: s.color, background: s.color }} />
											<b>{s.segment_name}</b>
										</td>
										<td className="cop-num">{s.audience_size.toLocaleString()}</td>
										<td><Sparkline data={s.trend} width={56} height={20} tone="purple" /></td>
										<td className="cop-num">{s.conversion_rate}%</td>
										<td><StatusPill status={s.status} /></td>
									</tr>
								))}
							</tbody>
						</table>
						</div>
					</Card>

					{isInternal && (
						<InternalCard title="Today's Activity" icon="◷" right="agent + job telemetry">
							{commandCenter.activity.map((a, i) => (
								<div className="cop-feed-item" key={i}>
									<span className="cop-feed-time">{a.time}</span>
									<span className="cop-feed-dot" style={{ color: TONE_COLOR[a.tone], background: TONE_COLOR[a.tone] }} />
									<span style={{ flex: 1 }}>{a.text}</span>
									<Pill tone="dim">{a.tag}</Pill>
								</div>
							))}
							<div className="cop-aop-links">
								<AopLink view="workflow-runs">AOP runs</AopLink>
								<AopLink view="audit-log">AOP audit</AopLink>
							</div>
						</InternalCard>
					)}
				</div>

				<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					<Card title="Pending AI Actions" icon="⧖" right={<button className="cop-link" onClick={() => navigate('approvals')}>View queue →</button>}>
						{pending.length === 0 && (
							<div style={{ color: 'var(--text-dim)', fontSize: 12, padding: '8px 0' }}>
								Queue is clear — no actions awaiting review.
							</div>
						)}
						{pending.map((a) => (
							<div className="cop-pending-item" key={a.approval_request_id}>
								<div className="cop-pending-text">
									<div className="t">{a.justification.split('—')[0].trim()}</div>
									<div className="s">{isInternal ? `${a.agent} · ` : 'Reviewed proposal · '}{a.impact_chip}</div>
								</div>
								<button className="cop-btn small primary" onClick={() => decideApproval(a.approval_request_id, 'approved')}>
									Approve
								</button>
							</div>
						))}
					</Card>

					<Card title="Live Experiments" icon="⚗" right={<button className="cop-link" onClick={() => navigate('experiment-lab')}>Open lab →</button>}>
						{commandCenter.live_experiments.map((e) => (
							<div className="cop-pending-item" key={e.name}>
								<div className="cop-pending-text">
									<div className="t">{e.name}</div>
									<div className="s">{e.sub}</div>
								</div>
								<span className={`cop-num tone-${e.tone}`} style={{ fontSize: 14 }}>{e.value}</span>
							</div>
						))}
					</Card>

					{isInternal && (
						<InternalCard title="System Health" icon="♥">
							{commandCenter.health.map((h) => (
								<div className="cop-health-row" key={h.label}>
									<span>{h.label}</span>
									<span className="cop-health-val">
										<span style={{ color: h.ok ? 'var(--green)' : 'var(--red)', fontSize: 8 }}>●</span>
										{h.value}
									</span>
								</div>
							))}
						</InternalCard>
					)}

					{isInternal && (
						<InternalCard title="Ops & Data Health" icon="⇄" right={internalOps.queue}>
							{internalOps.connections.map((c) => (
								<div className="cop-health-row" key={c.name}>
									<span>{c.name}</span>
									<span className="cop-health-val">
										<span style={{ color: c.ok ? 'var(--green)' : 'var(--red)', fontSize: 8 }}>●</span>
										{c.lag}
									</span>
								</div>
							))}
							<div className="cop-health-row">
								<span>Token spend</span>
								<span className="cop-health-val">{internalOps.tokens}</span>
							</div>
							<div style={{ marginTop: 6 }}>
								{internalOps.warnings.map((w, i) => (
									<WarnRow key={i} level={w.level}>{w.text}</WarnRow>
								))}
							</div>
							<div className="cop-aop-links">
								<AopLink view="overview">AOP overview</AopLink>
								<AopLink view="pending-actions">AOP actions</AopLink>
							</div>
						</InternalCard>
					)}
				</div>
			</div>
		</main>
	);
}
